import React, { useId, useState } from 'react';
import type { MouseEvent } from 'react';
import styled from '@emotion/styled';
import type { Nullable } from 'corgi-types';
import { height } from 'styled-system';

import Drawer from 'common/components/Drawer';
import { Flex } from 'common/components/Flex';
import { EventBusEvent } from 'common/enums/EventBus';
import { ReclamationStep } from 'common/enums/Reclamation';
import eventBus from 'common/event-bus/EventBus';
import { useKeyboardClose } from 'common/hooks/keyboard';
import { useNextToggler, useStepForm } from 'common/hooks/step-form';
import { useSubscription } from 'common/hooks/subscription';
import { useLocalization } from 'common/providers/LocaleProvider';
import type { WithArticleProps } from 'common/types/reclamation';
import { useDrawerAlert } from '../hooks/alert';
import { useDrawerNavigationHandler } from '../hooks/drawer';
import type { ReclamationDrawerAction, Step } from '../types/drawer';
import type { StepSubmitHandler } from '../types/stepSubmitHandler';
import { getCommonTrackingEventData } from '../utils/tracking';
import ReclamationStepAdditionalInfo from './reclamation-step-additional-info/ReclamationStepAdditionalInfo';
import ReclamationDrawerBody from './ReclamationDrawerBody';
import ReclamationDrawerFooter from './ReclamationDrawerFooter';
import type { ReclamationDrawerHeaderProps } from './ReclamationDrawerHeader';
import ReclamationDrawerHeader from './ReclamationDrawerHeader';
import ReclamationStepReasons from './ReclamationStepReasons';
import ReclamationStepSolutions from './ReclamationStepSolutions';

export interface ReclamationDrawerProps {
  shopName?: string;
}

type UseHeaderPropsReturnType = [
  ReclamationDrawerHeaderProps,
  (value: Partial<ReclamationDrawerHeaderProps>) => void,
  () => void,
];

const ReclamationDrawerForm = styled(Flex)<any>`
  ${height};
`;

const reclamationDrawerFormProps = {
  as: 'form',
  flexDirection: 'column',
  height: '100%',
};

const defaultSteps: Array<WithArticleProps<Step>> = [];

const stepMap: Record<ReclamationStep, React.ComponentType<Step>> = {
  [ReclamationStep.AdditionalInfo]: ReclamationStepAdditionalInfo,
  [ReclamationStep.Reasons]: ReclamationStepReasons,
  [ReclamationStep.Solutions]: ReclamationStepSolutions,
};

const getStepsFromSubscription = (state: Nullable<ReclamationDrawerAction>) =>
  (state?.isOpen && state.steps) || defaultSteps;

const getStepComponent = (activeStep: Step) => (activeStep ? stepMap[activeStep.type] : null);

const useHeaderProps = (defaultProps: ReclamationDrawerHeaderProps): UseHeaderPropsReturnType => {
  const [headerProps, setHeaderProps] = useState(defaultProps);
  const resetHeaderProps = () => setHeaderProps(defaultProps);
  const setProps = (additionalProps: Partial<ReclamationDrawerHeaderProps>) =>
    setHeaderProps({ ...defaultProps, ...additionalProps });

  return [headerProps, setProps, resetHeaderProps];
};

const usePreviousButtonLabel = (): [string, (title?: string) => void, () => void, () => void] => {
  const { r, t } = useLocalization();

  const [titles, setTitles] = useState<string[]>([]);

  const previousButtonLabel =
    titles.length === 0
      ? t('h24_back_to_article_selection')
      : r.t('h24_go_back_to_step', {
          step: titles[titles.length - 1],
        });

  const pushTitle = (title?: string) => {
    if (title) {
      setTitles([...titles, title]);
    }
  };

  const popTitle = () => {
    if (titles.length > 0) {
      setTitles(titles.slice(0, -1));
    }
  };

  const resetTitles = () => setTitles([]);

  return [previousButtonLabel, pushTitle, popTitle, resetTitles];
};

/* eslint-disable max-lines-per-function */
// eslint-disable-next-line complexity
const ReclamationDrawer = ({ shopName }: ReclamationDrawerProps) => {
  const ariaLabelId = useId();
  const { t } = useLocalization();
  const proceedButtonLabel = t('h24_proceed');
  const [isFooterVisible, setIsFooterVisible] = useState(true);
  const [isNextHidden, setIsNextHidden] = useState(false);
  const [hasInvalidField, setHasInvalidField] = useState(false);
  const [stepSubmitHandler, setStepSubmitHandler] = useState<StepSubmitHandler>(() =>
    Promise.resolve()
  );
  const [state] = useSubscription<ReclamationDrawerAction>(EventBusEvent.ReclamationDrawerToggle);
  const isOpen = !!state && state.isOpen;
  const [alert, setAlert] = useDrawerAlert(isOpen);

  const [previousButtonLabel, pushTitle, popTitle, resetTitles] = usePreviousButtonLabel();

  const [isNextDisabled, setIsNextDisabled] = useNextToggler(isOpen);
  const { activeStep, ...stepRestProps } = useStepForm(getStepsFromSubscription(state));
  const {
    drawerCloseHandler,
    backdropClickHandler,
    headerCloseClickHandler,
    goBackClickHandler,
    submitHandler,
  } = useDrawerNavigationHandler({
    state,
    eventBus,
    activeStep,
    setAlert,
    stepSubmitHandler,
    setIsNextDisabled,
    setHasInvalidField,
    ...stepRestProps,
  });

  const handleProceed = (event: MouseEvent) => {
    pushTitle(headerProps.title);
    submitHandler(event);
  };

  const handleGoBack = (event: MouseEvent) => {
    popTitle();
    goBackClickHandler(event);
  };

  const handleClose = (closeHandler: () => void) => () => {
    resetTitles();
    closeHandler();
  };

  const [headerProps, setHeaderProps, resetHeaderProps] = useHeaderProps({
    id: ariaLabelId,
    buttonLabel: t('h24_close'),
    buttonIcon: 'close',
    onClick: handleClose(headerCloseClickHandler),
  });

  useKeyboardClose(isOpen, handleClose(drawerCloseHandler));

  const StepComponent = getStepComponent(activeStep);
  const { article } = activeStep ?? {};
  const commonTrackingData = state?.trackingEventData
    ? state.trackingEventData
    : {
        button_text: proceedButtonLabel,
        ...getCommonTrackingEventData({
          state,
          activeStep,
        }),
      };

  return (
    <div data-corgi-id="reclamation-drawer">
      <Drawer
        isOpen={isOpen}
        onBackdropClick={handleClose(backdropClickHandler)}
        {...(headerProps.title ? { ariaLabelledby: ariaLabelId } : {})}
      >
        {isOpen && (
          <ReclamationDrawerForm
            data-testid="reclamation-drawer"
            onSubmit={handleProceed}
            {...reclamationDrawerFormProps}
          >
            <ReclamationDrawerHeader {...headerProps} />
            {StepComponent && (
              <ReclamationDrawerBody
                shopName={shopName}
                claimedQuantity={activeStep?.claimedQuantity ?? 0}
                productName={article?.name ?? ''}
                productVariantName={article?.variantName ?? ''}
                productImage={article?.images?.[0]?.path ?? null}
              >
                <StepComponent
                  {...activeStep}
                  alert={alert}
                  hasInvalidField={hasInvalidField}
                  trackingEventData={commonTrackingData}
                  setHeaderProps={setHeaderProps}
                  resetHeaderProps={resetHeaderProps}
                  setOnSubmit={setStepSubmitHandler}
                  setIsNextDisabled={setIsNextDisabled}
                  setIsNextHidden={setIsNextHidden}
                  setIsFooterVisible={setIsFooterVisible}
                />
              </ReclamationDrawerBody>
            )}
            {isFooterVisible && (
              <ReclamationDrawerFooter
                previousHref="#"
                previousAriaLabel={previousButtonLabel}
                isProceedDisabled={isNextDisabled}
                isProceedHidden={isNextHidden}
                proceedButtonText={proceedButtonLabel}
                onBackButtonClick={handleGoBack}
              />
            )}
          </ReclamationDrawerForm>
        )}
      </Drawer>
    </div>
  );
};

export default ReclamationDrawer;
